import { FunctionComponent, MouseEventHandler, ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

import { Spinner } from "../Spinner/Spinner";
import { getButtonColorClasses, getButtonStatusClasses, usesAccentFill } from "../Button/styles";
import { ButtonColor, ButtonSize, ButtonStatus, FontSize } from "../Button/types";

interface HoveredButtonProps {
	title: string;
	onClick?: MouseEventHandler;
	type?: "submit" | "reset" | "button";
	disabled?: boolean;
	disabledInGray?: boolean;
	status?: ButtonStatus;
	color?: ButtonColor;
	size?: ButtonSize;
	fontSize?: FontSize;
	isLoading?: boolean;
	image?: ReactElement;
	className?: string;
	testId?: string;
	iconRight?: boolean;
	ref?: React.RefObject<HTMLButtonElement>;
}

export const HoveredButton: FunctionComponent<HoveredButtonProps> = ({
	title,
	onClick,
	type = "submit",
	status,
	disabled,
	disabledInGray,
	isLoading,
	color = ButtonColor.BRAND,
	fontSize = FontSize.SM,
	size = ButtonSize.M,
	image,
	className,
	testId,
	iconRight,
	ref,
}) => {
	const { t } = useTranslation();
	const getSizeClasses = () => {
		switch (size) {
			case ButtonSize.XXS:
				return "h-[24px] w-[24px] px-1 text-xs";
			case ButtonSize.XS:
				return "h-[28px] px-2 text-xs";
			case ButtonSize.S:
				return "h-[32px] px-4 text-xs";
			case ButtonSize.M:
				return "h-[38px] px-4";
			case ButtonSize.ML:
				return "h-[42px] px-4";
			case ButtonSize.L:
				return "h-[48px] px-6";
		}
	};

	const getFontSizeClasses = () => {
		// §6.2 — the CTA's 16px is load-bearing, not decoration: a smaller white
		// label on #FF5A24 fails contrast. Where the accent fill is in play the
		// size is fixed at 16px regardless of what the caller asked for.
		if (usesAccentFill(color, size)) {
			return "text-[16px]";
		}

		switch (fontSize) {
			case FontSize.XS:
				return "text-xs";
			case FontSize.SM:
				return "text-sm";
			case FontSize.M:
				return "text-[16px]";
			default:
				return "";
		}
	};

	return (
		<button
			ref={ref}
			className={classNames(
				"group relative",
				"appearance-none font-semibold rounded-btn border duration-200 shadow-sm",
				"overflow-hidden !p-0",
				"min-w-[42px] max-w-[42px] hover:max-w-[400px] hover:min-w-fit",
				"transition-[max-width] duration-[250ms] ease-in-out",
				getButtonColorClasses(color, size, disabledInGray),
				getButtonStatusClasses(status),
				getSizeClasses(),
				getFontSizeClasses(),
				disabled ? "opacity-60" : "",
				disabledInGray ? "disabled:bg-border-soft disabled:text-muted" : "",
				className || ""
			)}
			data-clicktarget="tableaction"
			data-test={testId}
			disabled={isLoading || disabled}
			type={type}
			onClick={onClick}
		>
			<span
				className="absolute inset-y-0 left-1/2 -translate-x-1/2
                         group-hover:left-3 group-hover:translate-x-0
                         transition-[left,transform] duration-[250ms] ease-in-out
                         flex items-center [&_*]:!m-0"
			>
				{isLoading ? <Spinner /> : image}
			</span>

			<span
				className="pl-9 pr-4 whitespace-nowrap
                         opacity-0 group-hover:opacity-100
                         transition-opacity duration-[200ms] ease-in-out delay-[60ms]"
			>
				{isLoading ? t("basics.wait") : title}
			</span>
		</button>
	);
};
